import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

interface User {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
}

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
}

interface WorkOrderItem {
  id: number;
  structureId: number;
  Structure: Structure;
}

interface OrderClient {
  id: number;
  name: string;
}

interface Order {
  id: number;
  orderNumber: string;
  Client: OrderClient;
}

interface WorkOrder {
  id: number;
  workOrderNumber: string;
  orderId: number;
  type: 'placement' | 'videoReport';
  scheduledDate: string;
  deadline: string;
  status: 'pending' | 'inProgress' | 'completed' | 'cancelled';
  assignedToId: number | null;
  mediaUrl?: string;
  comments?: string;
  createdAt: string;
  updatedAt: string;
  Order?: Order;
  AssignedTo?: User;
  WorkOrderItems?: WorkOrderItem[];
}

interface PaginatedWorkOrdersResponse {
  workOrders: WorkOrder[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

interface PaginatedUsersResponse {
  users: User[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const STATUS_OPTIONS = {
  all: 'Все статусы',
  pending: 'Ожидает исполнения',
  inProgress: 'В процессе',
  completed: 'Выполнен',
  cancelled: 'Отменен'
};

const TYPE_OPTIONS = {
  all: 'Все типы',
  placement: 'Размещение рекламы',
  videoReport: 'Видеоотчет'
};

const WorkOrderList: React.FC = () => {
  const { can } = useAuth(); // Changed from isAllowed
  const [searchParams, setSearchParams] = useSearchParams();

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableTechnicians, setAvailableTechnicians] = useState<User[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [assigneeFilter, setAssigneeFilter] = useState(searchParams.get('assignee') || 'all');
  const [limit] = useState(20);

  const canCreate = can('workOrders.create'); // Changed from isAllowed
  const canViewDetails = can('workOrders.view'); // Changed from isAllowed
  const canEdit = can('workOrders.edit'); // Changed from isAllowed

  const fetchTechnicians = useCallback(async () => {
    try {
      const response = await axios.get<PaginatedUsersResponse>('/users', {
        params: {
          role: 'Технический специалист',
          limit: 1000
        }
      });
      if (response.data && Array.isArray(response.data.users)) {
        setAvailableTechnicians(response.data.users);
      } else {
        console.error('Invalid data structure received for technicians:', response.data);
        setAvailableTechnicians([]);
      }
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setAvailableTechnicians([]);
    }
  }, []);

  const fetchWorkOrders = useCallback(async (page = currentPage, search = searchTerm, status = statusFilter, type = typeFilter, assignee = assigneeFilter) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);
    if (type && type !== 'all') params.append('type', type);
    if (assignee && assignee !== 'all') params.append('assignedToId', assignee);

    const orderIdFromQuery = searchParams.get('orderId');
    if (orderIdFromQuery) {
      params.append('orderId', orderIdFromQuery);
    }

    const currentSearchParams = new URLSearchParams();
    currentSearchParams.set('page', String(page));
    if (search) currentSearchParams.set('search', search);
    if (status && status !== 'all') currentSearchParams.set('status', status);
    if (type && type !== 'all') currentSearchParams.set('type', type);
    if (assignee && assignee !== 'all') currentSearchParams.set('assignee', assignee);
    if (orderIdFromQuery) currentSearchParams.set('orderId', orderIdFromQuery);

    setSearchParams(currentSearchParams, { replace: true });

    try {
      const response = await axios.get<PaginatedWorkOrdersResponse>('/work-orders', { params });
      setWorkOrders(response.data.workOrders);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error('Error fetching work orders:', err);
      setError('Ошибка при загрузке нарядов');
      setWorkOrders([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, statusFilter, typeFilter, assigneeFilter]);

  useEffect(() => {
    fetchTechnicians();
    fetchWorkOrders(currentPage, searchTerm, statusFilter, typeFilter, assigneeFilter);
  }, [fetchTechnicians, fetchWorkOrders, currentPage, searchTerm, statusFilter, typeFilter, assigneeFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setAssigneeFilter('all');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Неверная дата';
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return 'Ошибка даты';
    }
  };

  const getStatusText = (status: keyof typeof STATUS_OPTIONS | undefined): string => {
    return status ? (STATUS_OPTIONS[status] || status) : '-';
  };

  const getStatusBadgeClass = (status: keyof typeof STATUS_OPTIONS | undefined): string => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status) {
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'inProgress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeText = (type: keyof typeof TYPE_OPTIONS | undefined): string => {
    return type ? (TYPE_OPTIONS[type] || type) : '-';
  };

  const getTypeBadgeClass = (type: keyof typeof TYPE_OPTIONS | undefined): string => {
    if (!type) return 'bg-gray-100 text-gray-800';
    switch (type) {
      case 'placement': return 'bg-indigo-100 text-indigo-800';
      case 'videoReport': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isDeadlineCritical = (deadline: string | undefined | null, status: string | undefined): boolean => {
    if (!deadline || status === 'completed' || status === 'cancelled') return false;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const deadlineDate = new Date(deadline);
      if (isNaN(deadlineDate.getTime())) return false;
      deadlineDate.setHours(0, 0, 0, 0);
      return deadlineDate <= today;
    } catch (e) {
      return false;
    }
  };

  const getAssignedTechnicianName = (assignedToId: number | null): string => {
    if (assignedToId === null) return 'Не назначен';
    if (!Array.isArray(availableTechnicians)) return `ID: ${assignedToId} (Загрузка...)`;

    const technician = availableTechnicians.find(tech => tech.id === assignedToId);
    return technician ? `${technician.firstName || ''} ${technician.lastName || ''}`.trim() : `ID: ${assignedToId}`;
  };

  if (loading && (!workOrders || workOrders.length === 0)) {
    return <LoadingSpinner />;
  }

  const orderIdFromQueryForTitle = searchParams.get('orderId');

  return (
    <div>
      <PageHeader
        title={orderIdFromQueryForTitle ? `Наряды для заказа #${orderIdFromQueryForTitle}` : "Наряды на работы"}
        subtitle={`Всего найдено: ${totalCount}`}
        actions={
          canCreate ? (
            <Link
              to={orderIdFromQueryForTitle ? `/work-orders/new?orderId=${orderIdFromQueryForTitle}` : "/work-orders/new"}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Создать наряд
            </Link>
          ) : null
        }
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium">Фильтры</h3>
          <button onClick={resetFilters} className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center" disabled={loading}>
            <XMarkIcon className="h-4 w-4 mr-1" />
            Сбросить все
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="search" className="form-label">Поиск</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              <input
                id="search"
                type="text"
                placeholder="Номер наряда, заказа, клиент..."
                className="form-input pl-10 w-full"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div>
            <label htmlFor="statusFilter" className="form-label">Статус</label>
            <select
              id="statusFilter"
              className="form-input w-full"
              value={statusFilter}
              onChange={(e) => handleFilterChange(setStatusFilter, e.target.value)}
            >
              {Object.entries(STATUS_OPTIONS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="typeFilter" className="form-label">Тип</label>
            <select
              id="typeFilter"
              className="form-input w-full"
              value={typeFilter}
              onChange={(e) => handleFilterChange(setTypeFilter, e.target.value)}
            >
              {Object.entries(TYPE_OPTIONS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="assigneeFilter" className="form-label">Исполнитель</label>
            <select
              id="assigneeFilter"
              className="form-input w-full"
              value={assigneeFilter}
              onChange={(e) => handleFilterChange(setAssigneeFilter, e.target.value)}
            >
              <option value="all">Все исполнители</option>
              <option value="null">Не назначен</option>
              {Array.isArray(availableTechnicians) && availableTechnicians.map(tech => (
                <option key={tech.id} value={tech.id}>
                  {tech.firstName || ''} {tech.lastName || ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && !error && (!workOrders || workOrders.length === 0) && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || assigneeFilter !== 'all')
              ? 'Наряды, соответствующие критериям, не найдены.'
              : 'Наряды на работу еще не созданы.'}
          </p>
          {canCreate && (
            <Link to="/work-orders/new" className="mt-4 btn-primary inline-flex items-center">
              <PlusIcon className="h-5 w-5 mr-1" />
              Создать наряд
            </Link>
          )}
        </div>
      )}

      {!loading && workOrders && workOrders.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Наряд №</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заказ</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Тип</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">План. Дата</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дедлайн</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Исполнитель</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {workOrders.map((wo) => (
                <tr key={wo.id} className={`hover:bg-gray-50 ${isDeadlineCritical(wo.deadline, wo.status) ? 'bg-red-50' : ''}`}>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm font-medium text-gray-900 break-words">
                    <Link to={`/work-orders/${wo.id}`} className="text-primary hover:underline">
                      {wo.workOrderNumber || `#${wo.id}`}
                    </Link>
                  </td>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm break-words">
                    {wo.Order ? (
                      <Link to={`/orders/${wo.orderId}`} className="text-primary hover:underline">
                        #{wo.Order.orderNumber} ({wo.Order.Client?.name || '?'})
                      </Link>
                    ) : (
                      <span className="text-gray-400">#{wo.orderId}</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-xs">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${getTypeBadgeClass(wo.type)}`}>
                      {getTypeText(wo.type)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{formatDate(wo.scheduledDate)}</td>
                  <td className={`px-4 py-4 text-sm ${isDeadlineCritical(wo.deadline, wo.status) ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                    {formatDate(wo.deadline)}
                  </td>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm text-gray-600 break-words">{getAssignedTechnicianName(wo.assignedToId)}</td>
                  <td className="px-4 py-4 text-xs">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${getStatusBadgeClass(wo.status)}`}>
                      {getStatusText(wo.status)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium space-x-2">
                    {canViewDetails && (
                      <Link to={`/work-orders/${wo.id}`} className="text-gray-600 hover:text-gray-900 p-1" title="Просмотр">
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    )}
                    {canEdit && (
                      <Link to={`/work-orders/edit/${wo.id}`} className="text-blue-600 hover:text-blue-900 p-1" title="Редактировать">
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default WorkOrderList;
